/* eslint-disable react/no-unused-prop-types */
import { css, cx } from 'emotion';
import { path, uniqBy } from 'ramda';
import React, { AnimationEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { Chip } from '@ezugi/primitives';
import { DeviceInfo } from '@ezugi/utils';

import { flyingChipsActions } from '../../store/reducers/flyingChips';
import { flyingChipsSelector } from '../../store/selectors/flyingChips';

type AnimateChipProps = {
  name: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
};

function getAnimation(props: AnimateChipProps): string {
  const { name, fromX, fromY, toX, toY } = props;
  const keyframes = `
        0% {
            transform: translate(${fromX}px, ${fromY}px);
        }
        100% {
            transform: translate(${toX}px, ${toY}px);
        }
    `;
  const animation = `
        .${name} {
            animation: ${name} 1s ease-in-out;
        }
    `;

  return [ `@keyframes ${name} {${keyframes}}`, animation ].join('\n');
}

const isSameAnimation = (
  prevProps: AnimateChipProps,
  nextProps: AnimateChipProps
) => prevProps.name === nextProps.name;

const AnimateChip = React.memo(
  (props: AnimateChipProps) => (
    <style key={props.name}>{getAnimation(props)}</style>
  ),
  isSameAnimation
);

export const FLYING_CHIP_DIRECTION = {
  BET_SPOT: 'BET_SPOT',
  CHIP_SELECTOR: 'CHIP_SELECTOR',
} as const;

export type FlyingChipDirection = keyof typeof FLYING_CHIP_DIRECTION;

export type FlyingChipProps = {
  animation: AnimateChipProps;
  value: number;
  img: string;
  chipSize: number;
  id: string;
  betType: string;
  direction: FlyingChipDirection;
};

const flyingChipContainer = css({
  willChange: 'transform',
  position: 'fixed',
  top: 0,
  left: 0,
});

const DEFAULT_CHIP_SIZE = 40;

const FlyingChip = (props: FlyingChipProps) => (
  <div
    id={props.id}
    className={cx(flyingChipContainer, props.animation.name)}
    style={{ width: props.chipSize, height: props.chipSize }}
  >
    <Chip value={props.value} img={props.img} size={props.chipSize} />
  </div>
);

export type ChipHolder = HTMLDivElement | null;

type ChipCoords = {
  x: number;
  y: number;
};

type ChipRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

declare const window: {
  scaleRatio: number
} & Window;

function getChipHolderRect(chipHolder: ChipHolder) {
  if (!chipHolder) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }

  const { left, top, width, height } = chipHolder.getBoundingClientRect();

  if (DeviceInfo.isHandheld) {
    return {
      left,
      top,
      width,
      height,
    };
  }

  const { left: parentLeft, top: parentTop } = document.getElementById('app')!.getBoundingClientRect();
  const offsetY = (top - parentTop) / window.scaleRatio;
  const offsetX = (left - parentLeft) / window.scaleRatio;

  return {
    left: offsetX,
    top: offsetY,
    width,
    height,
  };
}

function getChipHolderCoords(
  rect: DOMRect | ChipRect,
  chipSize: number
): ChipCoords {
  return {
    x: Math.round(rect.left + (rect.width - chipSize) / 2),
    y: Math.round(rect.top + (rect.height - chipSize) / 2),
  };
}

function getCoordinates(startRect: ChipRect, targetRect: ChipRect, chipSize = DEFAULT_CHIP_SIZE) {
  return {
    start: getChipHolderCoords(startRect, chipSize),
    target: getChipHolderCoords(targetRect, chipSize),
    chipSize,
  };
}

function getAnimationName(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
) {
  return `flying-chip-${fromX}-${fromY}-${toX}-${toY}`;
}

function getFlyingChipParams(
  startRef: ChipHolder,
  targetRef: ChipHolder,
  value: number,
  img: string,
  betType: string,
  direction: FlyingChipDirection,
  chipSizeInput?: number
): FlyingChipProps {
  const { start, target, chipSize } = getCoordinates(
    getChipHolderRect(startRef),
    getChipHolderRect(targetRef),
    chipSizeInput
  );

  const animation = {
    name: getAnimationName(start.x, start.y, target.x, target.y),
    fromX: start.x,
    fromY: start.y,
    toX: target.x,
    toY: target.y,
  };

  return {
    value,
    img,
    id: uuid(),
    animation,
    chipSize,
    betType,
    direction,
  };
}

export const createFlyingChipToBetSpot = (
  value: number,
  img: string,
  betType: string,
  chipSize?: number,
) => {
  const temp = getFlyingChipParams(
    document.querySelector('.flying-chips.chip-selector__chip-container--selected'),
    document.getElementById(`bet-spot-${betType}`) as HTMLDivElement,
    value,
    img,
    betType,
    FLYING_CHIP_DIRECTION.BET_SPOT,
    chipSize
  );

  return temp;
};

export const createFlyingChipToChipSelector = (
  value: number,
  img: string,
  betType: string,
  chipSize?: number,
) => {
  const temp = getFlyingChipParams(
    document.getElementById(`bet-spot-${betType}`) as HTMLDivElement,
    document.querySelector('.flying-chips.chip-selector__chip-container--selected'),
    value,
    img,
    betType,
    FLYING_CHIP_DIRECTION.CHIP_SELECTOR,
    chipSize
  );

  return temp;
};

export const FlyingChips = () => {
  const dispatch = useDispatch();

  const flyingChips = useSelector(flyingChipsSelector);

  const removeFlyingChip = (id: string) => {
    dispatch(flyingChipsActions.removeFlyingChip({ id }));
  };

  const onAnimationEndCapture = (event: AnimationEvent<HTMLDivElement>) => {
    removeFlyingChip((event.target as HTMLDivElement).id);
  };

  const animations = uniqBy(path([ 'animation', 'name' ]), flyingChips).map(
    (chip) => chip.animation
  );

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 4,
        top: 0,
      }}
      onAnimationEndCapture={onAnimationEndCapture}
    >
      {flyingChips.map((chip) => (
        <FlyingChip key={chip.id} {...chip} />
      ))}

      {animations.map((animation) => (
        <AnimateChip key={animation.name} {...animation} />
      ))}
    </div>
  );
};
